import { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

import { useAppContext } from "@/context/AppContext";
import { ConnectWiseAdapter } from "@/lib/adapters/connectwise";
import { buildDefaultRegistry } from "@/lib/adapters/registry";
import { parseCsv } from "@/lib/csv/parser";
import { runPipeline } from "@/lib/processing/pipeline";

/** Bundled sample (served from web/public — same-origin static asset, no external network). */
const SAMPLE_URL = "/sample_input.csv";
const SAMPLE_NAME = "sample_input.csv";

export interface SampleLoader {
  loadSample: () => Promise<void>;
  loading: boolean;
}

/**
 * One-click "Try with sample data": fetches the bundled sample CSV, auto-detects
 * the adapter, processes it, and navigates to the Process page.
 */
export function useSampleData(): SampleLoader {
  const navigate = useNavigate();
  const { config, setProcessed } = useAppContext();
  const [loading, setLoading] = useState(false);

  const loadSample = useCallback(async () => {
    setLoading(true);
    const toastId = toast.loading("Loading sample catalog…");
    try {
      const response = await fetch(SAMPLE_URL);
      if (!response.ok) throw new Error(`Sample not available (HTTP ${response.status})`);
      const text = await response.text();
      const file = new File([text], SAMPLE_NAME, { type: "text/csv" });
      const { headers, rows } = await parseCsv(file);
      const detection = buildDefaultRegistry().detect(headers);
      const adapter = detection?.adapter ?? new ConnectWiseAdapter();
      const result = runPipeline(rows, adapter, config);
      setProcessed({ fileName: SAMPLE_NAME, rawRows: rows, adapter }, result);
      toast.success(`Loaded sample — ${result.keptRows.toLocaleString()} rows kept`, {
        id: toastId,
        description: "Processed locally. Nothing was uploaded.",
      });
      navigate("/process");
    } catch (caught) {
      toast.error("Could not load the sample", {
        id: toastId,
        description: caught instanceof Error ? caught.message : String(caught),
      });
    } finally {
      setLoading(false);
    }
  }, [config, navigate, setProcessed]);

  return { loadSample, loading };
}
